import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import { DatasetId } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useDownloadExperience } from "../../../api/experience-files/download-experience";
import { useLoggedInUser } from "../../../api/user-auths/login";
import { isDialogOpen, setDialog } from "../../../store/slices/global";
import { IAppState } from "../../../store/types/store";
import theme from "../../../theme";
import { dataExport } from "../../../utils/linkSheet";
import openNewTab from "../../../utils/openNewTab";

interface DataExportDialogProps {
  showDataReconciliationExport?: boolean;
}
export const DataExportDialog = ({
  showDataReconciliationExport,
}: DataExportDialogProps) => {
  const isOpen = useSelector(isDialogOpen("dataExport"));
  const dispatch = useDispatch();
  const downloadExperience = useDownloadExperience();
  const user = useLoggedInUser();

  const experienceId = useSelector(
    (state: IAppState) => state.dashboardFilters.experienceId
  );

  const handleClose = () => {
    dispatch(setDialog({ dialog: "dataExport", open: false }));
  };

  const handleExperimentDownload = (value: DatasetId) => {
    if (experienceId && user.isSuccess) {
      downloadExperience.mutate({
        body: undefined,
        pathParams: {
          experienceId,
        },
        queryParams: {
          datasetId: value,
          sendTo: user.data.user.email,
        },
      });

      handleClose();
    }
  };

  useEffect(() => {
    if (downloadExperience.isSuccess) {
      handleClose();
    }
  }, [downloadExperience.isSuccess]);

  return (
    <IgDialog
      title={"Export Your Data"}
      handleClose={handleClose}
      handleYesClick={handleClose}
      showCloseIcon={true}
      showDialog={isOpen}
      dialogHeight={null}
      hideActions={true}
      rawContent={
        <Grid
          container
          direction={"column"}
          justifyContent={"center"}
          rowSpacing={2}
        >
          <Grid item>
            <IgTypography>
              Choose an option below and we will send you a downloadable link:
            </IgTypography>
          </Grid>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"center"}
            columnSpacing={2}
          >
            <Grid item>
              <IgButton
                loading={downloadExperience.isPending}
                variant={"outlined"}
                sx={{
                  color: theme.palette.gray.main,
                  border: `1px solid ${theme.palette.disabled.main}`,
                  backgroundColor: "white",
                }}
                onClick={() => {
                  handleExperimentDownload("lineItemsExport");
                }}
              >
                Line Items
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                sx={{
                  color: theme.palette.gray.main,
                  border: `1px solid ${theme.palette.disabled.main}`,
                  backgroundColor: "white",
                }}
                loading={downloadExperience.isPending}
                variant={"outlined"}
                onClick={() => {
                  handleExperimentDownload("ordersExport");
                }}
              >
                Orders
              </IgButton>
            </Grid>
            {showDataReconciliationExport && (
              <Grid item>
                <IgButton
                  sx={{
                    color: theme.palette.gray.main,
                    border: `1px solid ${theme.palette.disabled.main}`,
                    backgroundColor: "white",
                  }}
                  loading={downloadExperience.isPending}
                  variant={"outlined"}
                  onClick={() => {
                    handleExperimentDownload("ordersReconciliationExport");
                  }}
                >
                  Order Reconciliation
                </IgButton>
              </Grid>
            )}
          </Grid>
          <Grid item>
            <IgTypography>
              Learn more about exporting your data{" "}
              <IgTypography
                igStyle={"link"}
                onClick={() => openNewTab(dataExport, "Experiment Data Export")}
              >
                here.
              </IgTypography>
            </IgTypography>
          </Grid>
        </Grid>
      }
      loading={downloadExperience.isPending}
    />
  );
};
